import API_BASE_URL from '../config';

// API Base URLs
const API_URL = `${API_BASE_URL}/api`;

// Generic API request function
const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_URL}${endpoint}`;
  
  const defaultHeaders = {
    'Content-Type': 'application/json',
  };

  // Add Authorization header if token exists (only from sessionStorage)
  const token = sessionStorage.getItem('adminToken');
  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  const config = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);
    
    // Handle 503 Maintenance mode
    if (response.status === 503) {
      const maintenanceData = await response.json();
      throw {
        type: 'MAINTENANCE',
        data: maintenanceData
      };
    }

    // Handle authentication errors
    if (response.status === 401) {
      sessionStorage.removeItem('adminToken');
      window.location.href = '/admin/login';
      throw new Error('Session expired. Please login again.');
    }

    // Handle other errors
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
    
  } catch (error) {
    // Re-throw maintenance error
    if (error.type === 'MAINTENANCE') {
      throw error;
    }
    
    console.error('API Error:', error.message);
    throw error;
  }
};

// ==================== AUTH APIs ====================
export const adminLogin = (email, password) => {
  return apiRequest('/auth/admin/login', {
    method: 'POST',
    body: JSON.stringify({ email, password })
  });
};

export const getAdminProfile = () => {
  return apiRequest('/auth/admin/profile');
};

export const adminLogout = () => {
  return apiRequest('/auth/admin/logout', {
    method: 'POST'
  });
};

// ==================== MAINTENANCE APIs ====================
export const getMaintenanceStatus = () => {
  return apiRequest('/maintenance/status');
};

export const getAllMaintenance = () => {
  return apiRequest('/maintenance');
};

export const createMaintenance = (data) => {
  return apiRequest('/maintenance', {
    method: 'POST',
    body: JSON.stringify(data)
  });
};

export const updateMaintenance = (id, data) => {
  return apiRequest(`/maintenance/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
};

export const deleteMaintenance = (id) => {
  return apiRequest(`/maintenance/${id}`, {
    method: 'DELETE'
  });
};

// ✅ NEW: Send manual maintenance notification
export const sendMaintenanceNotification = (id) => {
  return apiRequest(`/maintenance/${id}/notify`, {
    method: 'POST'
  });
};

// ✅ NEW: Get upcoming maintenance for header
export const getUpcomingMaintenance = () => {
  return apiRequest('/maintenance/upcoming');
};

// ==================== DASHBOARD APIs ====================
export const getDashboardStats = () => {
  return apiRequest('/admin/dashboard');
};

export const getDeveloperCode = () => {
  return apiRequest('/admin/developer-code');
};

// ==================== USER MANAGEMENT APIs ====================
export const getAllUsers = () => {
  return apiRequest('/admin/users');
};

export const getUserById = (id) => {
  return apiRequest(`/admin/users/${id}`);
};

export const updateUserStatus = (userId, data) => {
  return apiRequest(`/admin/users/${userId}/status`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
};

export const deleteUser = (userId) => {
  return apiRequest(`/admin/users/${userId}`, {
    method: 'DELETE'
  });
};

export const searchUsers = (query) => {
  return apiRequest(`/admin/users/search?q=${encodeURIComponent(query)}`);
};

export const exportUsersToCSV = () => {
  return apiRequest('/admin/users/export');
};

// ==================== SUBSCRIPTION MANAGEMENT APIs ====================
export const updateUserSubscription = (userId, subscriptionData) => {
  return apiRequest(`/admin/users/${userId}/subscription`, {
    method: 'PUT',
    body: JSON.stringify(subscriptionData)
  });
};

export const extendUserTrial = (userId, days) => {
  return apiRequest(`/admin/users/${userId}/extend-trial`, {
    method: 'POST',
    body: JSON.stringify({ days })
  });
};

export const upgradeUserPlan = (userId, planData) => {
  return apiRequest(`/admin/users/${userId}/upgrade`, {
    method: 'POST',
    body: JSON.stringify(planData)
  });
};

// ==================== ANALYTICS APIs ====================
export const getUserAnalytics = (period = 'monthly') => {
  return apiRequest(`/admin/analytics/users?period=${period}`);
};

export const getSubscriptionAnalytics = () => {
  return apiRequest('/admin/analytics/subscriptions');
};

export const getRevenueAnalytics = (period = 'monthly') => {
  return apiRequest(`/admin/analytics/revenue?period=${period}`);
};

// ==================== SYSTEM APIs ====================
export const getSystemHealth = () => {
  return apiRequest('/admin/system/health');
};

export const getServerLogs = (limit = 100) => {
  return apiRequest(`/admin/system/logs?limit=${limit}`);
};

export const clearCache = () => {
  return apiRequest('/admin/system/clear-cache', {
    method: 'POST'
  });
};

// ==================== NOTIFICATION APIs ====================
export const sendBulkNotification = (notificationData) => {
  return apiRequest('/admin/notifications/send', {
    method: 'POST',
    body: JSON.stringify(notificationData)
  });
};

export const getNotifications = () => {
  return apiRequest('/admin/notifications');
};

// ==================== FILE UPLOAD ====================
export const uploadFile = async (file, endpoint = '/admin/upload') => {
  const formData = new FormData();
  formData.append('file', file);

  const token = sessionStorage.getItem('adminToken');
  
  const response = await fetch(`${API_URL}${endpoint}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData
  });

  if (!response.ok) {
    throw new Error('File upload failed');
  }

  return await response.json();
};

// ==================== BULK OPERATIONS ====================
export const bulkUpdateUsers = (userIds, updateData) => {
  return apiRequest('/admin/users/bulk-update', {
    method: 'POST',
    body: JSON.stringify({ userIds, updateData })
  });
};

export const bulkDeleteUsers = (userIds) => {
  return apiRequest('/admin/users/bulk-delete', {
    method: 'POST',
    body: JSON.stringify({ userIds })
  });
};

// ==================== UTILITY FUNCTIONS ====================
export const setAuthToken = (token) => {
  sessionStorage.setItem('adminToken', token);
};

export const clearAuthToken = () => {
  sessionStorage.removeItem('adminToken');
};

export const getAuthToken = () => {
  return sessionStorage.getItem('adminToken');
};

// Check if admin is authenticated
export const isAuthenticated = () => {
  return !!getAuthToken();
};

// Export API_URL if needed
export { API_URL };